import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AcademyEvent, EventBody } from '../domain/event';
import { eventsApi } from '../infrastructure/api';

export const eventKeys = {
  all: ['events'] as const,
  list: () => [...eventKeys.all, 'list'] as const,
  one: (id: string) => [...eventKeys.all, 'one', id] as const,
  registrations: (id: string) => [...eventKeys.all, 'registrations', id] as const,
};

export const eventsQuery = queryOptions({ queryKey: eventKeys.list(), queryFn: eventsApi.list });
export const eventQuery = (id: string) =>
  queryOptions({ queryKey: eventKeys.one(id), queryFn: () => eventsApi.get(id) });

export const useEvents = () => useQuery(eventsQuery);
export const useEvent = (id: string) => useQuery(eventQuery(id));
export const useRegistrations = (id: string) =>
  useQuery({ queryKey: eventKeys.registrations(id), queryFn: () => eventsApi.registrations(id) });

/** Toda escrita devolve o evento inteiro: grava no cache e recarrega a lista. */
function useEventMutation<V>(fn: (vars: V) => Promise<AcademyEvent>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (event) => {
      qc.setQueryData(eventKeys.one(event.id), event);
      void qc.invalidateQueries({ queryKey: eventKeys.list() });
    },
  });
}

export const useSaveEvent = () =>
  useEventMutation(({ id, body }: { id?: string; body: EventBody }) =>
    id ? eventsApi.update(id, body) : eventsApi.create(body),
  );

export const useEventStatus = () =>
  useEventMutation(({ id, action }: { id: string; action: 'publish' | 'unpublish' | 'cancel' }) =>
    eventsApi[action](id),
  );

export function useDeleteEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => eventsApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: eventKeys.all }),
  });
}
